import { fmtDateTimeUtcOffset } from "./formatters";
import { getNativeSafeAsset } from "./platformAssets";

const ASSET_VERSION = (() => {
  try {
    return (typeof window !== "undefined" && window.APP_VERSION) ? String(window.APP_VERSION) : "2.216.08";
  } catch (_) {
    return "2.216.08";
  }
})();

function versionedClimateAsset(src = "") {
  if (!src) return "";
  const safeSrc = getNativeSafeAsset(src);
  return safeSrc.includes("?") ? `${safeSrc}&v=${encodeURIComponent(ASSET_VERSION)}` : `${safeSrc}?v=${encodeURIComponent(ASSET_VERSION)}`;
}

const CLIMATE_VISUALS = {
  amanhecerLimpo: { key: "amanhecer_limpo", src: versionedClimateAsset("/climate/amanhecer_limpo.webp"), label: "Amanhecer limpo" },
  amanhecerParcial: { key: "amanhecer_parcial", src: versionedClimateAsset("/climate/amanhecer_parcial.webp"), label: "Amanhecer com nuvens" },
  amanhecerNublado: { key: "amanhecer_nublado", src: versionedClimateAsset("/climate/amanhecer_nublado.webp"), label: "Amanhecer nublado" },
  sol: { key: "sol", src: versionedClimateAsset("/climate/sol.webp"), label: "Céu limpo" },
  calor: { key: "calor", src: versionedClimateAsset("/climate/calor.webp"), label: "Calor forte" },
  solEntreNuvens: { key: "sol_entre_nuvens", src: versionedClimateAsset("/climate/sol_entre_nuvens.webp"), label: "Sol entre nuvens" },
  nublado: { key: "nublado", src: versionedClimateAsset("/climate/nublado.webp"), label: "Nublado" },
  anoitecerLimpo: { key: "anoitecer_limpo", src: versionedClimateAsset("/climate/anoitecer_limpo.webp"), label: "Anoitecer limpo" },
  anoitecerParcial: { key: "anoitecer_parcial", src: versionedClimateAsset("/climate/anoitecer_parcial.webp"), label: "Anoitecer com nuvens" },
  anoitecerNublado: { key: "anoitecer_nublado", src: versionedClimateAsset("/climate/anoitecer_nublado.webp"), label: "Anoitecer nublado" },
  garoa: { key: "garoa", src: versionedClimateAsset("/climate/garoa.webp"), label: "Garoa" },
  chuvaFraca: { key: "chuva_fraca", src: versionedClimateAsset("/climate/chuva_fraca.webp"), label: "Chuva fraca" },
  chuvaForte: { key: "chuva_forte", src: versionedClimateAsset("/climate/chuva_forte.webp"), label: "Chuva forte" },
  tempestade: { key: "tempestade", src: versionedClimateAsset("/climate/tempestade.webp"), label: "Tempestade" },
  neblina: { key: "neblina", src: versionedClimateAsset("/climate/neblina.webp"), label: "Neblina" },
  vento: { key: "vento", src: versionedClimateAsset("/climate/vento.webp"), label: "Vento forte" },
  frio: { key: "frio", src: versionedClimateAsset("/climate/frio.webp"), label: "Frio" },
  chuvaComSol: { key: "chuva_com_sol", src: versionedClimateAsset("/climate/chuva_com_sol.webp"), label: "Chuva com sol" },
  noiteLimpa: { key: "noite_limpa", src: versionedClimateAsset("/climate/noite_limpa.webp"), label: "Noite limpa" },
  parcialNubladoNoite: { key: "parcial_nublado_noite", src: versionedClimateAsset("/climate/parcial_nublado_noite.webp"), label: "Noite parcialmente nublada" },
  nubladoNoite: { key: "nublado_noite", src: versionedClimateAsset("/climate/nublado_noite.webp"), label: "Noite nublada" },
  geral: { key: "geral", src: versionedClimateAsset("/climate/sol_entre_nuvens.webp"), label: "Condição climática geral" },
};

function safeLower(value) {
  return String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function normalizeObservedDate(recordedAt = "") {
  const raw = String(recordedAt || "").trim();
  if (!raw) return null;
  const normalized = /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?)?$/.test(raw) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)
    ? raw.replace(" ", "T") + "Z"
    : raw;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function resolveUtcOffsetHours(...values) {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return -3;
}

function buildLocalDate(recordedAt = "", utcOffsetHours = null) {
  const date = normalizeObservedDate(recordedAt);
  if (!date) return null;
  const offsetHours = resolveUtcOffsetHours(utcOffsetHours);
  return new Date(date.getTime() + offsetHours * 3600000);
}

function inferLocalHour(recordedAt = "", utcOffsetHours = null) {
  const localDate = buildLocalDate(recordedAt, utcOffsetHours);
  if (!localDate) return null;
  const parts = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", hourCycle: "h23", timeZone: "UTC" }).formatToParts(localDate);
  const hour = Number(parts.find((part) => part.type === "hour")?.value);
  return Number.isFinite(hour) ? hour : null;
}

function dayOfYearFromUtcDate(date) {
  const start = Date.UTC(date.getUTCFullYear(), 0, 0);
  const current = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return Math.floor((current - start) / 86400000);
}

function computeSolarTimes({ recordedAt = "", utcOffsetHours = null, latitude = null, longitude = null } = {}) {
  const localDate = buildLocalDate(recordedAt, utcOffsetHours);
  const lat = Number(latitude);
  const lon = Number(longitude);
  const tzOffsetHours = resolveUtcOffsetHours(utcOffsetHours);
  if (!localDate || latitude == null || longitude == null || !Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  const dayOfYear = dayOfYearFromUtcDate(localDate);
  if (!Number.isFinite(dayOfYear) || dayOfYear < 1) return null;
  const gamma = (2 * Math.PI / 365) * (dayOfYear - 1);
  const eqtime = 229.18 * (0.000075 + 0.001868 * Math.cos(gamma) - 0.032077 * Math.sin(gamma) - 0.014615 * Math.cos(2 * gamma) - 0.040849 * Math.sin(2 * gamma));
  const decl = 0.006918 - 0.399912 * Math.cos(gamma) + 0.070257 * Math.sin(gamma) - 0.006758 * Math.cos(2 * gamma) + 0.000907 * Math.sin(2 * gamma) - 0.002697 * Math.cos(3 * gamma) + 0.00148 * Math.sin(3 * gamma);
  const latRad = lat * Math.PI / 180;
  const zenith = 90.833 * Math.PI / 180;
  const cosHourAngle = (Math.cos(zenith) / (Math.cos(latRad) * Math.cos(decl))) - (Math.tan(latRad) * Math.tan(decl));
  if (!Number.isFinite(cosHourAngle)) return null;
  if (cosHourAngle <= -1 || cosHourAngle >= 1) return null;
  const hourAngleDeg = Math.acos(cosHourAngle) * 180 / Math.PI;
  const solarNoonMinutes = 720 - (4 * lon) - eqtime + (tzOffsetHours * 60);
  const sunriseMinutes = solarNoonMinutes - (hourAngleDeg * 4);
  const sunsetMinutes = solarNoonMinutes + (hourAngleDeg * 4);
  return { sunriseMinutes, sunsetMinutes };
}

function inferDayPhase({ recordedAt = "", utcOffsetHours = null, latitude = null, longitude = null, hour = null } = {}) {
  const localDate = buildLocalDate(recordedAt, utcOffsetHours);
  if (!localDate) return "day";
  const explicitHour = hour === null || hour === undefined || hour === ""
    ? null
    : Number(hour);
  const hasExplicitHour = Number.isFinite(explicitHour);
  const localMinutes = hasExplicitHour
    ? (explicitHour * 60) + localDate.getUTCMinutes()
    : (localDate.getUTCHours() * 60) + localDate.getUTCMinutes();
  const solarTimes = computeSolarTimes({ recordedAt, utcOffsetHours, latitude, longitude });
  if (!solarTimes) {
    const localHour = hasExplicitHour ? explicitHour : inferLocalHour(recordedAt, utcOffsetHours);
    if (!Number.isFinite(localHour)) return "day";
    if (localHour < 6 || localHour >= 18) return "night";
    if (localHour < 7) return "dawn";
    if (localHour >= 17) return "dusk";
    return "day";
  }
  const dawnStart = solarTimes.sunriseMinutes - 35;
  const dawnEnd = solarTimes.sunriseMinutes + 45;
  const duskStart = solarTimes.sunsetMinutes - 45;
  const duskEnd = solarTimes.sunsetMinutes + 30;
  if (localMinutes < dawnStart) return "night";
  if (localMinutes <= dawnEnd) return "dawn";
  if (localMinutes < duskStart) return "day";
  if (localMinutes <= duskEnd) return "dusk";
  return "night";
}


function visualForSky(phase = "day", variant = "clear") {
  const map = {
    clear: {
      dawn: CLIMATE_VISUALS.amanhecerLimpo,
      day: CLIMATE_VISUALS.sol,
      dusk: CLIMATE_VISUALS.anoitecerLimpo,
      night: CLIMATE_VISUALS.noiteLimpa,
    },
    partial: {
      dawn: CLIMATE_VISUALS.amanhecerParcial,
      day: CLIMATE_VISUALS.solEntreNuvens,
      dusk: CLIMATE_VISUALS.anoitecerParcial,
      night: CLIMATE_VISUALS.parcialNubladoNoite,
    },
    cloudy: {
      dawn: CLIMATE_VISUALS.amanhecerNublado,
      day: CLIMATE_VISUALS.nublado,
      dusk: CLIMATE_VISUALS.anoitecerNublado,
      night: CLIMATE_VISUALS.nubladoNoite,
    },
  };
  return map[variant]?.[phase] || map[variant]?.day || CLIMATE_VISUALS.sol;
}

function pickVisualByText(conditionText = "", dayPhase = "day") {
  const text = safeLower(sanitizeConditionTextForPhase(conditionText, dayPhase));
  if (!text) return null;
  if (/(tempest|trovo|raio|storm|trovada|trovoada|temporal)/.test(text)) return CLIMATE_VISUALS.tempestade;
  if (/(vento|ventania|rajada|vendaval|wind)/.test(text)) return CLIMATE_VISUALS.vento;
  if (/(neblina|nevoa|fog|mist|bruma)/.test(text)) return CLIMATE_VISUALS.neblina;
  if (/(chuva com sol|sol com chuva|sunshower)/.test(text)) return CLIMATE_VISUALS.chuvaComSol;
  if (/(garoa|drizzle|chuvisco)/.test(text)) return CLIMATE_VISUALS.garoa;
  if (/(chuva forte|heavy rain|aguaceiro)/.test(text)) return CLIMATE_VISUALS.chuvaForte;
  if (/(chuva|rain|pancada)/.test(text)) return CLIMATE_VISUALS.chuvaFraca;
  if (/(encoberto|overcast|muito nublado|nublado)/.test(text)) return visualForSky(dayPhase, "cloudy");
  if (/(parcial|algumas nuvens|partly|sol entre nuvens|mostly cloudy|partly cloudy)/.test(text)) return visualForSky(dayPhase, "partial");
  if (/(limpo|ceu limpo|clear|ensolarado|sunny|aberto)/.test(text)) return visualForSky(dayPhase, "clear");
  return null;
}

function buildPendingVisualMeta(label = "Leitura climática pendente") {
  return { ...CLIMATE_VISUALS.geral, label };
}

function sanitizeConditionTextForPhase(conditionText = "", dayPhase = "day") {
  const raw = String(conditionText || "").trim();
  if (!raw) return "";
  if (dayPhase === "night") return raw;
  return raw
    .replace(/\b(?:night|overnight)\b/gi, "")
    .replace(/\bnoite\b/gi, "")
    .replace(/\bnoturna\b/gi, "")
    .replace(/\bnoturno\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function resolveCoords(payload = null) {
  const latest = payload?.latest_snapshot || payload?.latestSnapshot || {};
  const directLat = latest?.latitude ?? latest?.lat ?? payload?.latitude ?? payload?.lat ?? null;
  const directLon = latest?.longitude ?? latest?.lon ?? payload?.longitude ?? payload?.lon ?? null;
  if (directLat != null && directLon != null && Number.isFinite(Number(directLat)) && Number.isFinite(Number(directLon))) {
    return { latitude: Number(directLat), longitude: Number(directLon) };
  }
  const sourceStationId = String(latest?.source_station_id || latest?.sourceStationId || payload?.source_station_id || payload?.sourceStationId || "").trim();
  const sourceType = String(latest?.source_type || latest?.sourceType || payload?.effective_source || payload?.effectiveSource || "").trim();
  const statuses = Array.isArray(payload?.station_statuses) ? payload.station_statuses : [];
  const match = statuses.find((item) => {
    const itemStationId = String(item?.station_id || item?.stationId || "").trim();
    const itemSourceType = String(item?.source_type || item?.sourceType || "").trim();
    if (sourceStationId && itemStationId && sourceStationId === itemStationId) return true;
    return sourceType && itemSourceType && sourceType === itemSourceType && (!sourceStationId || !itemStationId);
  });
  const lat = match?.latitude ?? match?.lat ?? null;
  const lon = match?.longitude ?? match?.lon ?? null;
  return lat != null && lon != null && Number.isFinite(Number(lat)) && Number.isFinite(Number(lon)) ? { latitude: Number(lat), longitude: Number(lon) } : { latitude: null, longitude: null };
}


function resolveSummaryUtcOffset(payload = null) {
  const latest = payload?.latest_snapshot || payload?.latestSnapshot || {};
  const direct = resolveUtcOffsetHours(
    latest?.utc_offset_hours,
    latest?.utcOffsetHours,
    payload?.utc_offset_hours,
    payload?.utcOffsetHours,
  );
  const sourceStationId = String(latest?.source_station_id || latest?.sourceStationId || payload?.source_station_id || payload?.sourceStationId || "").trim();
  const statuses = Array.isArray(payload?.station_statuses) ? payload.station_statuses : [];
  const match = statuses.find((item) => String(item?.station_id || item?.stationId || "").trim() === sourceStationId) || null;
  return resolveUtcOffsetHours(match?.utc_offset_hours, match?.utcOffsetHours, direct);
}

export function pickClimateVisual({
  tempC = null,
  humidityPct = null,
  rainfallMm = null,
  windKph = null,
  conditionText = "",
  recordedAt = "",
  utcOffsetHours = null,
  isNight = null,
  dayPhase = null,
  latitude = null,
  longitude = null,
} = {}) {
  const phase = dayPhase || (typeof isNight === "boolean"
    ? (isNight ? "night" : inferDayPhase({ recordedAt, utcOffsetHours, latitude, longitude }))
    : inferDayPhase({ recordedAt, utcOffsetHours, latitude, longitude }));
  const night = phase === "night";
  const textDriven = pickVisualByText(conditionText, phase);
  if (textDriven) return textDriven;

  const rain = Number.isFinite(Number(rainfallMm)) ? Number(rainfallMm) : null;
  const temp = Number.isFinite(Number(tempC)) ? Number(tempC) : null;
  const humidity = Number.isFinite(Number(humidityPct)) ? Number(humidityPct) : null;
  const wind = Number.isFinite(Number(windKph)) ? Number(windKph) : null;

  if (wind != null && wind >= 35) return CLIMATE_VISUALS.vento;
  if (rain != null && rain >= 35) return CLIMATE_VISUALS.tempestade;
  if (rain != null && rain >= 15) return CLIMATE_VISUALS.chuvaForte;
  if (rain != null && rain >= 4) return CLIMATE_VISUALS.chuvaFraca;
  if (rain != null && rain > 0) {
    if (!night && temp != null && temp >= 28) return CLIMATE_VISUALS.chuvaComSol;
    return CLIMATE_VISUALS.garoa;
  }
  if (humidity != null && humidity >= 96) return CLIMATE_VISUALS.neblina;
  if (temp != null && temp < 16) return CLIMATE_VISUALS.frio;
  if (phase === "night") {
    if (humidity != null && humidity >= 95) return CLIMATE_VISUALS.nubladoNoite;
    if (humidity != null && humidity >= 82) return CLIMATE_VISUALS.parcialNubladoNoite;
    return CLIMATE_VISUALS.noiteLimpa;
  }
  if (temp != null && temp >= 35 && phase === "day") return CLIMATE_VISUALS.calor;
  if (humidity != null && humidity >= 88) return visualForSky(phase, "cloudy");
  if (humidity != null && humidity >= 72) return visualForSky(phase, "partial");
  return visualForSky(phase, "clear");
}

function buildObservedLabel(latest = {}) {
  const observed = String(latest?.last_recorded_at || latest?.lastRecordedAt || "").trim();
  return {
    observed,
    observedLabel: observed ? fmtDateTimeUtcOffset(observed, latest?.utc_offset_hours ?? latest?.utcOffsetHours ?? null) : "",
  };
}

export function buildClimateVisualMeta(summary = null) {
  const latest = summary?.latest_snapshot || summary?.latestSnapshot || {};
  const utcOffsetHours = resolveSummaryUtcOffset(summary || latest);
  const { observed, observedLabel } = buildObservedLabel({ ...latest, utc_offset_hours: utcOffsetHours });
  const coords = resolveCoords(summary || latest);
  const hasCurrentData = !!(observed || latest?.temp_current_c != null || latest?.humidity_current_pct != null || latest?.condition_label || latest?.conditionLabel);
  if (!hasCurrentData) {
    return {
      ...buildPendingVisualMeta(summary?.integration_status === "pending" ? "Leitura climática pendente" : "Sem condição climática consolidada"),
      observedLabel: "",
    };
  }
  const phase = inferDayPhase({ recordedAt: observed, utcOffsetHours, latitude: coords.latitude, longitude: coords.longitude });
  const visual = pickClimateVisual({
    tempC: latest?.temp_current_c ?? latest?.tempCurrentC ?? null,
    humidityPct: latest?.humidity_current_pct ?? latest?.humidityCurrentPct ?? null,
    rainfallMm: latest?.rainfall_mm ?? latest?.rainfallMm ?? null,
    windKph: latest?.wind_avg_kmh ?? latest?.windKph ?? null,
    conditionText: sanitizeConditionTextForPhase(latest?.condition_label || latest?.conditionLabel || "", phase),
    recordedAt: observed,
    utcOffsetHours,
    dayPhase: phase,
    latitude: coords.latitude,
    longitude: coords.longitude,
  });
  return {
    ...visual,
    label: visual?.label || (phase === "night" ? "Condição noturna" : "Condição diurna"),
    observedLabel,
  };
}

export function buildClimateMaxVisualMeta(summary = null) {
  const latest = summary?.latest_snapshot || summary?.latestSnapshot || {};
  const utcOffsetHours = resolveSummaryUtcOffset(summary || latest);
  const { observed, observedLabel } = buildObservedLabel({ ...latest, utc_offset_hours: utcOffsetHours });
  const coords = resolveCoords(summary || latest);
  const tempMax = latest?.temp_max_c ?? latest?.tempMaxC ?? null;
  const humidityForMax = latest?.humidity_max_pct ?? latest?.humidityMaxPct ?? latest?.humidity_current_pct ?? latest?.humidityCurrentPct ?? null;
  if (!observed && tempMax == null && humidityForMax == null) {
    return { ...buildPendingVisualMeta("Máxima do dia pendente"), observedLabel: "" };
  }
  let visual = pickClimateVisual({
    tempC: tempMax,
    humidityPct: humidityForMax,
    rainfallMm: latest?.rainfall_mm ?? latest?.rainfallMm ?? null,
    conditionText: sanitizeConditionTextForPhase(latest?.condition_label || latest?.conditionLabel || "", "day"),
    recordedAt: observed,
    utcOffsetHours,
    dayPhase: "day",
    latitude: coords.latitude,
    longitude: coords.longitude,
  });
  if (Number.isFinite(Number(tempMax)) && Number(tempMax) >= 33) visual = CLIMATE_VISUALS.calor;
  else if (Number.isFinite(Number(tempMax)) && Number(tempMax) < 16) visual = CLIMATE_VISUALS.frio;
  return {
    ...visual,
    observedLabel,
  };
}

export function buildClimateDailyVisualMeta(item = {}) {
  const tempMin = item?.temp_min_c == null || item?.temp_min_c === "" ? null : Number(item?.temp_min_c);
  const tempMax = item?.temp_max_c == null || item?.temp_max_c === "" ? null : Number(item?.temp_max_c);
  const avgTemp = tempMin == null && tempMax == null ? null : Number((((tempMin ?? tempMax ?? 0) + (tempMax ?? tempMin ?? 0)) / 2).toFixed(1));
  const rainfallMm = item?.rainfall_mm == null || item?.rainfall_mm === "" ? null : Number(item?.rainfall_mm);
  const humidityPct = item?.humidity_avg_pct ?? item?.humidity_max_pct ?? item?.humidityAvgPct ?? item?.humidityMaxPct ?? null;
  const conditionText = String(item?.condition_label || item?.conditionLabel || item?.summary || item?.notes || "").trim();
  let visual = pickClimateVisual({
    tempC: tempMax ?? avgTemp,
    humidityPct,
    rainfallMm,
    conditionText: sanitizeConditionTextForPhase(conditionText, "day"),
    recordedAt: item?.observed_on ? `${String(item.observed_on).trim()}T12:00:00Z` : "",
    dayPhase: "day",
  });
  if (Number.isFinite(Number(tempMax ?? avgTemp)) && Number(tempMax ?? avgTemp) < 16) visual = CLIMATE_VISUALS.frio;
  if (Number.isFinite(Number(tempMax ?? avgTemp)) && Number(tempMax ?? avgTemp) >= 33) visual = CLIMATE_VISUALS.calor;
  if ((Number.isFinite(Number(tempMax ?? avgTemp)) && Number(tempMax ?? avgTemp) >= 22) && visual?.key === "frio") visual = CLIMATE_VISUALS.solEntreNuvens;
  return visual || CLIMATE_VISUALS.geral;
}

export function buildForecastVisualMeta(item = {}) {
  const tempMax = item?.temp_max_c ?? item?.tempMaxC ?? null;
  const tempMin = item?.temp_min_c ?? item?.tempMinC ?? null;
  const rainMm = item?.rainfall_mm ?? item?.rainfallMm ?? null;
  const chance = item?.rain_chance_pct ?? item?.rainChancePct ?? null;
  const label = String(item?.condition_label || item?.conditionLabel || item?.condition || "").trim();
  const isoDate = String(item?.date || item?.observed_on || item?.observedOn || "").trim();
  const lowered = safeLower(label);
  const explicitNight = lowered.includes("noite") || lowered.includes("night");
  const dayPhase = explicitNight ? "night" : "day";
  return pickClimateVisual({
    tempC: tempMax ?? tempMin,
    rainfallMm: rainMm != null ? rainMm : (Number.isFinite(Number(chance)) && Number(chance) >= 65 ? 5 : 0),
    conditionText: label,
    recordedAt: isoDate ? `${isoDate}T12:00:00Z` : "",
    dayPhase,
  });
}

export function climateVisualsCatalog() {
  return CLIMATE_VISUALS;
}
